/**
 * Allys Property Map View
 * 
 * Theme-specific wrapper for PropertyMapView with Allys branding and styling
 * Provides Leaflet map integration and Allys-styled property cards
 */

'use client';

import { LeafletMap } from '../../../../src/components/maps';
import type { MapRendererProps } from '../../../../src/components/properties/PropertyMapView';
import { PropertyMapView } from '../../../../src/components/properties/PropertyMapView';
import type { Property } from '../../../../src/lib/api/clients/properties';
import { AllysPropertyMapCard } from './AllysPropertyMapCard';

export interface AllysPropertyMapViewProps {
  /**
   * Properties to display
   */
  properties: Property[];
  /**
   * Organisation ID for generating links
   */
  organisationId: string;
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
  /**
   * Optional labels for translations
   */
  labels?: {
    bedrooms?: string;
    bathrooms?: string;
    area?: string;
    viewDetails?: string;
  };
  /**
   * Map position: 'left' or 'right'
   * @default 'right'
   */
  mapPosition?: 'left' | 'right';
  /**
   * Empty state message
   */
  emptyMessage?: string;
  /**
   * Height of the map view container
   * @default 'calc(100vh - 320px)'
   */
  height?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Base path for property links (e.g., '/site/org-123' for tenant sites)
   * If not provided, defaults to '/{locale}'
   */
  basePath?: string;
}

/**
 * Custom Allys Map Renderer with OpenStreetMap
 * 
 * Uses Leaflet with OpenStreetMap tiles (free and open source)
 */
function AllysMapRenderer({ 
  properties, 
  selectedPropertyId,
  hoveredPropertyId,
  onMarkerClick,
  className = '',
}: MapRendererProps) {
  return (
    <div className={`w-full h-full relative ${className}`}>
      <LeafletMap
        properties={properties}
        selectedPropertyId={selectedPropertyId}
        {...(hoveredPropertyId !== undefined && { hoveredPropertyId })}
        onMarkerClick={onMarkerClick}
        className="w-full h-full"
      />
    </div>
  );
}

/**
 * AllysPropertyMapView - Airbnb-styled map view with Allys theming
 * 
 * Features:
 * - Interactive map with property markers (right side)
 * - Scrollable property list (left side)
 * - Hover and selection syncing between map and list
 * - Modern Allys color scheme and styling
 */
export function AllysPropertyMapView({
  properties,
  organisationId,
  locale = 'en',
  labels,
  mapPosition = 'right',
  emptyMessage,
  height = 'calc(100vh - 320px)',
  className = '',
  basePath,
}: AllysPropertyMapViewProps) {
  // Default basePath to /{locale} if not provided
  const effectiveBasePath = basePath || `/${locale}`;
  
  // Map property locations (mock data for now)
  const locationMap: Record<string, string> = {
    '1': 'Grand Baie',
    '2': 'Black River',
    '3': 'Tamarin',
    '4': 'Belle Mare',
    '5': 'Flic en Flac',
    '6': 'Bel Ombre',
    '7': 'Trou aux Biches',
    '8': 'Port Louis',
    '9': 'Moka',
  };

  return (
    <PropertyMapView
      properties={properties}
      organisationId={organisationId}
      locale={locale}
      mapPosition={mapPosition}
      height={height}
      {...(emptyMessage && { emptyMessage })}
      className={`rounded-2xl overflow-hidden border-2 border-gray-200 shadow-lg ${className}`}
      renderMap={(props) => <AllysMapRenderer {...props} />}
      renderCard={(property, isSelected) => (
        <AllysPropertyMapCard
          property={property}
          locationName={locationMap[property.id] || 'Mauritius'}
          {...(labels && { labels })}
          href={`${effectiveBasePath}/properties/${property.id}`}
          isSelected={isSelected}
        />
      )}
    />
  );
}

